import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { useMutation } from '@apollo/client/react';

import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { CancelSubscriptionDocument } from 'src/graphql/actions/billing/cancelSubscription.mutation';
import { CreateFeedbackDocument } from 'src/graphql/actions/profile/createFeedback.mutation';
import { useCancellationPlan } from 'src/context/billing';
import env from 'env';

const feedbackSchema = z.object({
  reasons: z.array(z.string()).refine((value) => value.some((item) => item), {
    message: 'You have to select at least one item.',
  }),
  comments: z.string().optional(),
  terms: z.boolean().refine((value) => value, {
    message: 'You have to agree to the terms and conditions.',
  }),
  otherTerms: z.boolean().refine((value) => value, {
    message: 'You have to agree to the terms and conditions.',
  }),
});

export type IFeedbackForm = z.infer<typeof feedbackSchema>;

const items = [
  'No longer needed',
  'Codekeeper isn’t working for our needs',
  'We found an alternative provider',
  'Subscription costs',
  'Technical issue',
  'Other',
];

const DowngradeInProgress = () => {
  const {
    ui: { setStep },
  } = useCancellationPlan();
  const [createFeedback] = useMutation(CreateFeedbackDocument);
  const [cancelSubscription] = useMutation(CancelSubscriptionDocument);
  const form = useForm<IFeedbackForm>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: {
      reasons: [],
      comments: '',
      terms: false,
      otherTerms: false,
    },
    mode: 'onChange',
  });

  const onSubmit = async ({ reasons, comments }: IFeedbackForm) => {
    let messageText = reasons.map((reason) => `- ${reason};\n`).join('');

    if (comments) messageText += `- Comments: ${comments}`;

    try {
      await createFeedback({
        variables: {
          message: messageText,
        },
      });
      await cancelSubscription();
      setStep('cancellation-success');
    } catch {
      toast.error('Something went wrong');
    }
  };

  const onUndo = () => setStep('keep-plan');

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div className="bg-accent border border-border rounded-lg shadow-sm p-6">
          <div className="space-y-1.5 mb-6">
            <h6 className="text-base text-foreground font-medium">
              Before you go, please share your reason for canceling?
            </h6>
            <p className="text-sm text-muted-foreground">
              Your feedback will help us make Codekeeper better for our other customers.
            </p>
          </div>
          <FormField
            control={form.control}
            name="reasons"
            render={() => (
              <FormItem>
                {items.map((item, index) => (
                  <FormField
                    key={index}
                    control={form.control}
                    name="reasons"
                    render={({ field }) => {
                      return (
                        <FormItem key={item} className="flex flex-row items-center gap-2">
                          <FormControl>
                            <Checkbox
                              checked={field.value?.includes(item)}
                              onCheckedChange={(checked) => {
                                return checked
                                  ? field.onChange([...field.value, item])
                                  : field.onChange(field.value?.filter((value: string) => value !== item));
                              }}
                            />
                          </FormControl>
                          <FormLabel className="text-sm text-foreground font-normal">
                            {item}{' '}
                            {index === items.length - 1 && <i>(please specify your reason in the comments below)</i>}
                          </FormLabel>
                        </FormItem>
                      );
                    }}
                  />
                ))}
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="comments"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Comments <i>(optional)</i>
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Please tell us more, we’d love to hear from you."
                    className="h-[106px] resize-none"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="terms"
            render={({ field }) => (
              <FormItem>
                <div className="flex flex-row items-center gap-2">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={(checked) => field.onChange(checked)} />
                  </FormControl>
                  <FormLabel className="text-sm text-foreground font-normal mb-0">
                    I agree to the{' '}
                    <a
                      href={env.CODEKEEPER_TERMS_URL}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-foreground! underline! hover:no-underline!"
                    >
                      terms and conditions
                    </a>
                    .
                  </FormLabel>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="otherTerms"
            render={({ field }) => (
              <FormItem>
                <div className="flex flex-row items-start gap-2">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(checked) => field.onChange(checked)}
                      className="mt-[2px]"
                    />
                  </FormControl>
                  <FormLabel className="text-sm text-foreground font-normal mb-0">
                    I agree that any data associated with this account will no longer be accessible to me or any of the
                    members of my organization.
                  </FormLabel>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex justify-end gap-2.5">
          <Button
            type="submit"
            variant="secondary"
            disabled={!form.formState.isValid || form.formState.isSubmitting}
            isLoading={form.formState.isSubmitting}
          >
            Cancel plan
          </Button>
          <Button onClick={onUndo}>Undo</Button>
        </div>
      </form>
    </Form>
  );
};

export default DowngradeInProgress;
